import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import apiService from '../../services/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { isIOSApp } from '../../utils/platform';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/Card';

const registerSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters').max(30, 'Username must not exceed 30 characters').regex(/^[a-zA-Z0-9]+$/, 'Username can only contain letters and numbers'),
  email: z.string().trim().email('Please enter a valid email address'),
  password: z.string().min(10, 'Password must be at least 10 characters'),
  confirmPassword: z.string(),
  inviteToken: z.string().optional(), // Make invitation token optional
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

function RegisterForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const { register: registerUser, error, clearError } = useAuth();
  const navigate = useNavigate();

  const inviteToken = searchParams.get('invite');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // Load invitation info when component mounts (if invite token exists)
  useEffect(() => {
    if (inviteToken) {
      // Set the invite token in the form
      setValue('inviteToken', inviteToken);
      
      // Fetch invitation details using apiService
      const fetchInvitationDetails = async () => {
        try {
          interface InvitationResponse {
            organisationName: string;
            invitedBy: string;
            invitedEmail?: string;
          }
          
          const response = await apiService.get<InvitationResponse>(`/auth/invite/${inviteToken}`);
          if (response.success && response.data) {
            setInvitationInfo(response.data);
            // Pre-fill email if provided in invitation
            if (response.data.invitedEmail) {
              setValue('email', response.data.invitedEmail);
            }
          } else {
            console.error('Invalid invitation');
          }
        } catch (error) {
          console.error('Failed to fetch invitation:', error);
        }
      };
      
      fetchInvitationDetails();
    }
  }, [inviteToken, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      clearError();
      await registerUser(data);
      setShowVerificationMessage(true);
      // Show verification message for 5 seconds, then redirect
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 5000);
    } catch {
      // Error is handled by the auth context
    } finally {
      setIsLoading(false);
    }
  };

  // App Store 3.1.1: the iOS app must carry no account registration at all.
  // An invite-only form was left in on 2026-09-22 and App Review rejected the
  // build again on 2026-09-24, so invitations are accepted on the web too and
  // the invited employee then signs in here. Android and the web keep both.
  if (isIOSApp()) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-neutral-50 via-white to-primary-50">
      <div className="w-full max-w-md space-y-8">
        <Card className="relative overflow-hidden">
          {/* Subtle background pattern */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent"></div>
          
          <CardHeader className="text-center relative">
            <CardTitle className="text-3xl">Create account</CardTitle>
            <CardDescription className="text-lg">
              Join us today and get started
            </CardDescription>
          </CardHeader>
          
          <CardContent className="relative">
            {invitationInfo && (
              <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">You're invited to join {invitationInfo.organisationName}!</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Invited by {invitationInfo.invitedBy} • Complete your registration below
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4 animate-slide-up">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-error-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-error-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-5">
                <Input
                  label="Username"
                  type="text"
                  autoComplete="username"
                  placeholder="Choose a username"
                  {...register('username')}
                  error={errors.username?.message}
                  hint="3-30 characters, letters and numbers only"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                />

                <Input
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Enter your email"
                  {...register('email')}
                  error={errors.email?.message}
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  }
                />

                <Input
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  {...register('password')}
                  error={errors.password?.message}
                  hint="Must be at least 10 characters"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                />

                <Input
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  {...register('confirmPassword')}
                  error={errors.confirmPassword?.message}
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
              </div>

              <div className="flex items-start space-x-3">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 mt-1 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="terms" className="text-sm text-neutral-700 leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
          </CardContent>
          
          {showVerificationMessage && (
            <div className="px-6 pb-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <svg className="h-5 w-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-green-800">Account created successfully!</h3>
                    <p className="text-sm text-green-700 mt-1">
                      A verification email has been sent to your inbox. Please verify your email to access all features.
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      Redirecting to dashboard in a few seconds...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <CardFooter>
            <p className="text-sm text-neutral-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                Sign in instead
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

export default RegisterForm;